import * as React from "react";

import type { Omittable } from "../lib/props";
import { useIsTouchDevice } from "./use-is-touch-device";

/**
 * The two-step tap for a target whose HOVER state hides a decision.
 *
 * On a fine pointer, hover shows the target's secondary actions and a click
 * performs the primary one; there is nothing to arbitrate. On a coarse pointer
 * hover never fires, so a single tap would commit to the primary action with
 * the alternatives never seen. This gate absorbs the FIRST tap to reveal
 * them (`absorb` returns `true`, the caller does nothing else) and lets the
 * SECOND through.
 *
 * It is the right tool only where several actions compete on one target — a
 * value cell whose hover row offers copy / clear / open beside "edit". A
 * CONTAINER whose hover reveals chrome (a row's kebab, a card's checkbox) is
 * the other shape and has its own mechanism: a held press arms the group
 * (`lib/touch-arm`) and the click it produces is spent, read off the DOM so
 * every handler in the group sees it.
 *
 * Gated on pointer capability, never viewport width: a tablet at a desktop
 * breakpoint still taps.
 *
 * `onRevealChange(true)` fires in the same render as the reveal, so a host
 * that dismisses on a highlight it tracks sees the two flip together — read
 * the stale `false` first and it cancels the reveal it just caused.
 */
export function useRevealOnTouchGate({
  enabled = true,
  onRevealChange,
}: Omittable<{
  /** `false` skips the reveal step: nothing to show, so the first tap goes
   *  straight through (an empty value has no value-actions). */
  enabled?: boolean;
  onRevealChange?: (revealed: boolean) => void;
}> = {}): {
  isTouch: boolean;
  revealed: boolean;
  setRevealed: React.Dispatch<React.SetStateAction<boolean>>;
  /** Call with the activation the target is about to perform. `true` means
   *  the tap was consumed by the reveal — perform nothing. */
  absorb: (next: boolean) => boolean;
  dismiss: () => void;
} {
  const isTouch = useIsTouchDevice();
  const [revealed, setRevealed] = React.useState(false);
  const absorb = React.useCallback(
    (next: boolean): boolean => {
      if (next && isTouch && enabled && !revealed) {
        setRevealed(true);
        onRevealChange?.(true);
        return true;
      }
      return false;
    },
    [isTouch, enabled, onRevealChange, revealed],
  );
  const dismiss = React.useCallback(() => {
    setRevealed(false);
    onRevealChange?.(false);
  }, [onRevealChange]);
  return { isTouch, revealed, setRevealed, absorb, dismiss };
}
